import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ROUTES } from "../../../config/routes";
import { onboardingClient } from "../../../trpc/onboarding/client";
import { STEPS, DOCUMENT_KEYS, type DocumentKey } from "./constants";
import { createInitialData, getDraftPayload, validateStep } from "./helpers";
import type { FormData, OnboardingCatalogs } from "./types";

const STORAGE_KEY = "palomma:onboarding";
const DRAFT_STORAGE_KEY = "palomma:onboarding-draft";
const EMPTY_CATALOGS: OnboardingCatalogs = {
  cities: [],
  erps: [],
  banks: [],
};

function loadStoredId(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw).applicationId ?? null;
  } catch {
    return null;
  }
}

function saveToStorage(applicationId: string) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ applicationId }));
}

function clearStorage() {
  localStorage.removeItem(STORAGE_KEY);
}

function loadStoredDraft(): Partial<FormData> | undefined {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<FormData>) : undefined;
  } catch {
    return undefined;
  }
}

function saveDraft(data: FormData) {
  localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data));
}

function clearDraft() {
  localStorage.removeItem(DRAFT_STORAGE_KEY);
}

export function useOnboarding() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get("id");
  const storedId = urlId || loadStoredId();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(storedId));
  const [error, setError] = useState<string | undefined>();
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [applicationStatus, setApplicationStatus] = useState("");
  const [customerFacingSummary, setCustomerFacingSummary] = useState<
    string | undefined
  >();
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState<
    Partial<Record<DocumentKey, string>>
  >({});
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [data, setData] = useState<FormData>(() => ({
    ...createInitialData(),
    ...loadStoredDraft(),
  }));
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState<
    string | undefined
  >();
  const [catalogs, setCatalogs] = useState<OnboardingCatalogs>(EMPTY_CATALOGS);
  const [isCatalogsLoading, setIsCatalogsLoading] = useState(true);
  const isReturned =
    applicationStatus === "draft" && Boolean(customerFacingSummary);
  const logoObjectUrlRef = useRef<string>();

  useEffect(() => {
    return () => {
      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void onboardingClient.onboarding.catalogs
      .query()
      .then((nextCatalogs) => {
        if (!cancelled) setCatalogs(nextCatalogs);
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(
            cause instanceof Error
              ? cause.message
              : "No se pudieron cargar las opciones del formulario."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsCatalogsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!applicationId) saveDraft(data);
  }, [applicationId, data]);

  const updateData = (field: string, value: string | boolean) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Shared reset: clear application/resume state, form data, and storage.
  const resetState = () => {
    clearStorage();
    clearDraft();
    setSearchParams({}, { replace: true });
    setApplicationId(null);
    setApplicationStatus("");
    setIsCompleted(false);
    setCurrentStep(1);
    setData(createInitialData());
    setUploadedDocs({});
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = undefined;
    }
    setLogoPreviewUrl(undefined);
    setFieldErrors({});
    setCustomerFacingSummary(undefined);
    setNotificationEmail(undefined);
    setError(undefined);
  };

  useEffect(() => {
    let cancelled = false;

    if (!storedId) {
      setApplicationId(null);
      setApplicationStatus("");
      setIsCompleted(false);
      setIsLoading(false);
      return;
    }

    // Already loaded/created in memory (e.g. right after start()) — don't refetch.
    if (storedId === applicationId) return;

    setIsLoading(true);
    setError(undefined);

    void onboardingClient.onboarding.get
      .query({ id: storedId })
      .then((application) => {
        if (cancelled) return;

        // Missing or cancelled → clear resume state and start a new application
        if (!application) {
          resetState();
          return;
        }

        const defaults = createInitialData();
        const nextUploadedDocs: Partial<Record<DocumentKey, string>> = {};

        for (const key of DOCUMENT_KEYS) {
          const fileName = application.documents[key];
          if (fileName) nextUploadedDocs[key] = fileName;
        }

        setApplicationId(application.id);
        setApplicationStatus(application.status);
        setCustomerFacingSummary(application.customerFacingSummary);
        setNotificationEmail(application.notificationEmail);
        setUploadedDocs(nextUploadedDocs);
        if (logoObjectUrlRef.current) {
          URL.revokeObjectURL(logoObjectUrlRef.current);
          logoObjectUrlRef.current = undefined;
        }
        setLogoPreviewUrl(undefined);
        const billing = application.billingInfo;
        setData({
          ...defaults,
          tipoPersona:
            billing?.documentType === "cc" ? "natural" : defaults.tipoPersona,
          nombre: billing?.name ?? "",
          nombreComercial: application.displayName ?? "",
          identificacion: billing?.documentNumber ?? "",
          representanteLegalNombre: application.legalRepresentative?.name ?? "",
          representanteLegalIdentificacion:
            application.legalRepresentative?.documentNumber ?? "",
          direccion: billing?.address ?? "",
          ciudad: billing?.city ?? "",
          otraCiudad: billing?.otherCity ?? "",
          ciiu: application.ciiu ?? "",
          web: application.website ?? "",
          celular: application.supportWhatsapp ?? defaults.celular,
          correoCliente: application.supportEmail ?? defaults.correoCliente,
          erp: application.erp ?? defaults.erp,
          otroErp: application.otherErp ?? "",
          contratos:
            application.contractCount !== undefined
              ? String(application.contractCount)
              : defaults.contratos,
          cartera:
            application.portfolioCop !== undefined
              ? String(application.portfolioCop)
              : defaults.cartera,
          modeloRecaudo:
            application.gateway === true
              ? "gateway"
              : application.gateway === false
              ? "agregador"
              : defaults.modeloRecaudo,
          codigoPse: application.pseServiceCode ?? "",
          banco: application.merchantAccount?.bank ?? defaults.banco,
          tipoCuenta:
            application.merchantAccount?.accountType ?? defaults.tipoCuenta,
          numeroCuenta:
            application.merchantAccount?.accountNumber ?? defaults.numeroCuenta,
          titularCuentaEsComercio:
            application.merchantAccount?.holderIsCompany ??
            defaults.titularCuentaEsComercio,
          titularCuentaNombre: application.merchantAccount?.holder?.name ?? "",
          titularCuentaTipoDocumento:
            application.merchantAccount?.holder?.documentType ?? "",
          titularCuentaNumeroDocumento:
            application.merchantAccount?.holder?.documentNumber ?? "",
          palommaPlus: application.palommaPlusEnabled ?? defaults.palommaPlus,
          correoFacturacion: billing?.email ?? defaults.correoFacturacion,
          whatsappFacturacion:
            billing?.phoneNumber ?? defaults.whatsappFacturacion,
          pep: application.pep ? "si" : "no",
          cripto: application.cryptoRelated ? "si" : "no",
          pepNombre: application.pepDetails?.name ?? "",
          pepCargo: application.pepDetails?.role ?? "",
          pepDetalle: application.pepDetails?.notes ?? "",
          criptoActividad: application.cryptoDetails?.activity ?? "",
          criptoRelacion: application.cryptoDetails?.relationship ?? "",
          criptoDetalle: application.cryptoDetails?.notes ?? "",
          origenFondos: application.acceptedSourceOfFunds ?? false,
          tratamientoDatos: application.acceptedDataPolicy ?? false,
          aceptaContrato: application.acceptedContract ?? false,
        });

        if (["submitted", "created"].includes(application.status)) {
          setIsCompleted(true);
        } else if (
          application.customerFacingSummary ||
          application.returnedAt
        ) {
          setCurrentStep(1);
          setIsCompleted(false);
        } else {
          setCurrentStep(
            Math.min((application.lastCompletedStep ?? 0) + 1, STEPS.length)
          );
          setIsCompleted(false);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          resetState();
          setError(
            cause instanceof Error
              ? cause.message
              : "No se pudo cargar la solicitud."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // resetState is recreated per render; re-running on it would restart the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedId, applicationId]);

  // The logo preview needs a presigned URL. Fetched in its own effect because
  // the load effect re-runs (and cancels) as soon as it sets applicationId,
  // which would drop an in-flight fetch started inside it.
  useEffect(() => {
    // A fresh local upload already shows its own object-URL preview.
    if (!applicationId || !uploadedDocs.logo || logoObjectUrlRef.current) {
      return;
    }
    let cancelled = false;
    void onboardingClient.onboarding.getLogoUrl
      .query({ id: applicationId })
      .then(({ url }) => {
        if (!cancelled) setLogoPreviewUrl(url);
      })
      .catch(() => {
        // Missing preview is cosmetic; the uploaded state still shows.
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId, uploadedDocs.logo]);

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const handleFileSelect = (docKey: DocumentKey) => {
    fileInputRefs.current[docKey]?.click();
  };

  const handleFileChange = async (
    docKey: DocumentKey,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file && (await uploadDocument(docKey, file)) && docKey === "logo") {
      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
      }
      const previewUrl = URL.createObjectURL(file);
      logoObjectUrlRef.current = previewUrl;
      setLogoPreviewUrl(previewUrl);
    }
  };

  const nextStep = () => {
    if (currentStep < STEPS.length) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    const resumeUrl = `${window.location.origin}${ROUTES.PUBLIC.ONBOARD}?id=${applicationId}`;
    try {
      await navigator.clipboard.writeText(resumeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = resumeUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReset = () => {
    resetState();
    setIsSubmitting(false);
    setIsSaving(false);
    setIsUploading(false);
  };

  const startApplication = async (email: string) => {
    setIsSaving(true);
    setError(undefined);
    try {
      const payload = getDraftPayload(data);
      const result = await onboardingClient.onboarding.start.mutate({
        notificationEmail: email,
        displayName: payload.displayName,
        erp: payload.erp,
        otherErp: payload.otherErp,
        contractCount: payload.contractCount,
        portfolioCop: payload.portfolioCop,
        ciiu: payload.ciiu,
        website: payload.website,
      });
      setNotificationEmail(email);
      setApplicationId(result.id);
      setApplicationStatus("draft");
      saveToStorage(result.id);
      clearDraft();
      setShowEmailModal(false);
      setSearchParams({ id: result.id }, { replace: true });
      setCurrentStep(2);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "No se pudo iniciar la solicitud."
      );
      throw cause;
    } finally {
      setIsSaving(false);
    }
  };

  const confirmEmailAndStart = async (email: string) => {
    setError(undefined);
    if (!applicationId) {
      try {
        await startApplication(email);
      } catch {
        // Error already surfaced in startApplication
      }
      return;
    }

    setIsSaving(true);
    try {
      const payload = getDraftPayload(data);
      await onboardingClient.onboarding.saveDraft.mutate({
        id: applicationId,
        step: 1,
        notificationEmail: email,
        displayName: payload.displayName,
        erp: payload.erp,
        otherErp: payload.otherErp,
        contractCount: payload.contractCount,
        portfolioCop: payload.portfolioCop,
      });
      setNotificationEmail(email);
      setShowEmailModal(false);
      setCurrentStep(2);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "No se pudo guardar el correo."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const saveCurrentStep = async () => {
    if (!applicationId || !notificationEmail) return;

    setIsSaving(true);
    setError(undefined);
    try {
      const payload = getDraftPayload(data);
      if (currentStep === 1) {
        await onboardingClient.onboarding.saveDraft.mutate({
          id: applicationId,
          step: 1,
          notificationEmail,
          displayName: payload.displayName,
          erp: payload.erp,
          otherErp: payload.otherErp,
          contractCount: payload.contractCount,
          portfolioCop: payload.portfolioCop,
        });
      } else if (currentStep === 2) {
        await onboardingClient.onboarding.saveDraft.mutate({
          id: applicationId,
          step: 2,
          notificationEmail,
          billingInfo: payload.billingInfo,
          legalRepresentative: payload.legalRepresentative,
          ciiu: payload.ciiu,
          website: payload.website,
        });
      } else if (currentStep === 3) {
        await onboardingClient.onboarding.saveDraft.mutate({
          id: applicationId,
          step: 3,
          notificationEmail,
          gateway: payload.gateway,
          pseServiceCode: payload.pseServiceCode,
          merchantAccount: payload.merchantAccount,
        });
      } else if (currentStep === 4) {
        await onboardingClient.onboarding.saveDraft.mutate({
          id: applicationId,
          step: 4,
          notificationEmail,
          palommaPlusEnabled: payload.palommaPlusEnabled,
          supportEmail: payload.supportEmail,
          supportWhatsapp: payload.supportWhatsapp,
        });
      } else if (currentStep === 5) {
        await onboardingClient.onboarding.saveDraft.mutate({
          id: applicationId,
          step: 5,
          notificationEmail,
          billingInfo: payload.billingInfo,
        });
      }
      nextStep();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "No se pudo guardar la información."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const uploadDocument = async (
    documentKey: DocumentKey,
    file: File
  ): Promise<boolean> => {
    if (!applicationId) {
      setError("Completa la información básica antes de cargar documentos.");
      return false;
    }

    setIsUploading(true);
    setError(undefined);
    try {
      const { uploadUrl } =
        await onboardingClient.onboarding.getUploadUrl.mutate({
          id: applicationId,
          documentKey,
        });
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!response.ok) throw new Error("No se pudo cargar el archivo.");

      await onboardingClient.onboarding.confirmUpload.mutate({
        id: applicationId,
        documentKey,
        fileName: file.name,
      });
      setUploadedDocs((prev) => ({ ...prev, [documentKey]: file.name }));
      setFieldErrors((prev) => {
        if (!prev[documentKey]) return prev;
        const next = { ...prev };
        delete next[documentKey];
        return next;
      });
      return true;
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "No se pudo cargar el archivo."
      );
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const showFieldErrors = (nextErrors: Record<string, string>) => {
    setFieldErrors(nextErrors);
    requestAnimationFrame(() => {
      document
        .querySelector('[data-onboarding-error="true"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const handleContinue = async () => {
    const nextErrors = validateStep(currentStep, data, uploadedDocs);
    if (Object.keys(nextErrors).length > 0) {
      showFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    if (currentStep === 1 && (!applicationId || !notificationEmail)) {
      setShowEmailModal(true);
    } else if (currentStep < STEPS.length) {
      await saveCurrentStep();
    }
  };

  const handleFinalSubmit = async () => {
    const nextErrors = validateStep(6, data, uploadedDocs);
    if (Object.keys(nextErrors).length > 0) {
      showFieldErrors(nextErrors);
      return;
    }
    if (!applicationId || !notificationEmail) return;

    setIsSubmitting(true);
    setError(undefined);
    try {
      const payload = getDraftPayload(data);
      await onboardingClient.onboarding.submit.mutate({
        id: applicationId,
        notificationEmail,
        ...payload,
      });
      setCustomerFacingSummary(undefined);
      setApplicationStatus("submitted");
      setIsCompleted(true);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "No se pudo enviar la solicitud."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasApplication = Boolean(applicationId);

  return {
    currentStep,
    isCompleted,
    isReturned,
    isLoading: isLoading || isCatalogsLoading,
    isSaving,
    isUploading,
    isSubmitting,
    error,
    applicationId,
    applicationStatus,
    customerFacingSummary,
    catalogs,
    logoPreviewUrl,
    data,
    uploadedDocs,
    fieldErrors,
    copied,
    showEmailModal,
    notificationEmail,
    hasApplication,
    updateData,
    handleFileSelect,
    handleFileChange,
    prevStep,
    handleContinue,
    handleFinalSubmit,
    handleReset,
    copyLink,
    confirmEmailAndStart,
    fileInputRefs,
    setShowEmailModal,
  };
}
